/** Lets footer navigation (arrows, dropdown, keys) pause so the unsaved-markings prompt can run. */

type InterceptHandler = (proceed: () => void) => boolean;

let interceptHandler: InterceptHandler | null = null;
let skipOnce = false;
let isAiQuestionMarked = false;
let isAiMarkingUnavailable = false;
const unavailableListeners = new Set<() => void>();

export const setAiQuestionMarked = (marked: boolean): void => {
    isAiQuestionMarked = marked;
};

/** Set while the Graide embed shows an error banner instead of the applet: nothing to confirm or lose. */
export const setAiMarkingUnavailable = (unavailable: boolean): void => {
    if (isAiMarkingUnavailable === unavailable) {
        return;
    }
    isAiMarkingUnavailable = unavailable;
    unavailableListeners.forEach((listener) => listener());
};

export const getAiMarkingUnavailable = (): boolean => isAiMarkingUnavailable;

export const subscribeAiMarkingUnavailable = (listener: () => void): (() => void) => {
    unavailableListeners.add(listener);
    return () => {
        unavailableListeners.delete(listener);
    };
};

const reloadListeners = new Set<() => void>();

export const requestAiMarkingReload = (): void => {
    reloadListeners.forEach((listener) => listener());
};

export const subscribeAiMarkingReload = (listener: () => void): (() => void) => {
    reloadListeners.add(listener);
    return () => {
        reloadListeners.delete(listener);
    };
};

export const setUnsavedMarkingsIntercept = (handler: InterceptHandler | null): void => {
    interceptHandler = handler;
};

/** Call immediately before a navigation that should not show the prompt (Done & Next / Save & Next). */
export const skipUnsavedMarkingsPromptOnce = (): void => {
    skipOnce = true;
};

/** Drop a skip that was not consumed (e.g. Done on the last hit, where navigateToNext is a no-op). */
export const clearUnsavedMarkingsPromptSkip = (): void => {
    skipOnce = false;
};

export const resetUnsavedMarkingsGuard = (): void => {
    interceptHandler = null;
    skipOnce = false;
    isAiQuestionMarked = false;
    isAiMarkingUnavailable = false;
};

/**
 * @returns true when navigation was held for the prompt; false when `proceed` should run now.
 */
export const tryInterceptUnsavedMarkingsNavigation = (proceed: () => void): boolean => {
    if (skipOnce) {
        skipOnce = false;
        return false;
    }
    if (!interceptHandler) {
        return false;
    }
    if (isAiQuestionMarked || isAiMarkingUnavailable) {
        return false;
    }
    return interceptHandler(proceed);
};
